import React from 'react';
import { Contact, ContactType } from 'ts-core';
import { useTheme } from '../..';

function ensureHttp(url: string): string {
    if (!url.startsWith('http://') && !url.startsWith('https://')) return 'https://' + url;
    return url;
}

const WebIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
);

const EmailIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
);

const PhoneIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
    </svg>
);

const TicketIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
    </svg>
);

const PersonIcon = ({ color }: { color: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke={color} className="w-4 h-4 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
);

export interface ContactInfoListProps {
    contacts: Contact[];
    className?: string;
}

/**
 * Renders a list of contact info items (website, email, phone, landline, ticket)
 * with optional name header and ticket print info footer.
 * Used by both ContactSection (event-level) and LocationSection (venue-level).
 */
export function ContactInfoList({ contacts, className }: ContactInfoListProps) {
    const { primaryColor: color } = useTheme();

    if (!contacts || contacts.length === 0) return null;

    const contactName = contacts.find(c => c.type === ContactType.Name);
    const ticketPrintInfo = contacts.find(c => c.type === ContactType.TicketPrintInfo);

    const rows: React.ReactNode[] = [];

    const website = contacts.find(c => c.type === ContactType.Website);
    if (website?.content) {
        const href = ensureHttp(website.content);
        rows.push(
            <a key="website" href={href} target="_blank" rel="noopener noreferrer"
               style={{ color }} className="flex items-center gap-2 no-underline hover:underline text-sm">
                <WebIcon />{website.content}
            </a>
        );
    }

    const email = contacts.find(c => c.type === ContactType.Email);
    if (email?.content && email.content !== 'hallo@balloon-events.com' && email.content !== 'hello@balloon-events.com') {
        rows.push(
            <a key="email" href={`mailto:${email.content}`}
               style={{ color }} className="flex items-center gap-2 no-underline hover:underline text-sm">
                <EmailIcon />{email.content}
            </a>
        );
    }

    const phone = contacts.find(c => c.type === ContactType.Phone);
    if (phone?.content) {
        rows.push(
            <a key="phone" href={`tel:${phone.content}`}
               style={{ color }} className="flex items-center gap-2 no-underline hover:underline text-sm">
                <PhoneIcon />{phone.content}
            </a>
        );
    }

    const landline = contacts.find(c => c.type === ContactType.Landline);
    if (landline?.content) {
        rows.push(
            <a key="landline" href={`tel:${landline.content}`}
               style={{ color }} className="flex items-center gap-2 no-underline hover:underline text-sm">
                <PhoneIcon />{landline.content}
            </a>
        );
    }

    const ticket = contacts.find(c => c.type === ContactType.Ticket);
    if (ticket?.content) {
        const href = ensureHttp(ticket.content);
        rows.push(
            <a key="ticket" href={href} target="_blank" rel="noopener noreferrer"
               style={{ color }} className="flex items-center gap-2 no-underline hover:underline text-sm">
                <TicketIcon />{ticket.content}
            </a>
        );
    }

    if (rows.length === 0 && !contactName && !ticketPrintInfo) return null;

    return (
        <div className={`flex flex-col gap-2${className ? ` ${className}` : ''}`}>
            {contactName?.content && (
                <div className="flex items-center gap-2 text-base font-medium">
                    <PersonIcon color={color} />
                    {contactName.content}
                </div>
            )}
            {rows}
            {ticketPrintInfo?.content && (
                <div className="text-sm text-gray-500 mt-1">{ticketPrintInfo.content}</div>
            )}
        </div>
    );
}
